import "./Editor.css";

import { Editor } from "@tiptap/react";
import React, { useCallback, useRef, useState } from "react";

import {
  Grid,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { MuiColorInput } from "mui-color-input";

import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import CodeIcon from "@mui/icons-material/Code";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import TerminalIcon from "@mui/icons-material/Terminal";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import WrapTextIcon from "@mui/icons-material/WrapText";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { IMenuBarProps } from "./models";
import { InsertLinkButton } from "./InsertLinkButton/InsertLinkButton";
import { Menu, MenuItem, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import TableChartIcon from "@mui/icons-material/TableChart";
import ViewWeekIcon from "@mui/icons-material/ViewWeek"; // coluna
import ViewStreamIcon from "@mui/icons-material/ViewStream"; // linha
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BorderAllIcon from "@mui/icons-material/BorderAll";

const MenuBar: React.FC<{
  editor: Editor | null;
  menuBarProps?: IMenuBarProps;
  variables?: { name: string; code: string }[];
}> = ({
  editor,
  variables = [],
  menuBarProps = {
    fontSize: true,
    markFormatting: true,
    selectColor: true,
    textFormatting: true,
    linkFormatting: true,
    addImageFromUpload: true,
    tableFormatting: true,
  },
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (!editor) {
    return null;
  }

  const open = Boolean(anchorEl);

  const handleClickVariable = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseVariable = () => {
    setAnchorEl(null);
  };

  const insertVariable = (code: string) => {
    if (editor) {
      editor.chain().focus().insertContent(` {{${code}}} `).run();
    }
    handleCloseVariable();
  };

  // eslint-disable-next-line
  const addImage = useCallback(() => {
    const url = window.prompt("Enter image URL:");

    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  const handleAlignment = (newAlignment: string | null) => {
    if (newAlignment !== null) {
      editor.chain().focus().setTextAlign(newAlignment).run();
    }
  };

  const currentAlignment = () => {
    if (editor.isActive({ textAlign: "left" })) return "left";
    if (editor.isActive({ textAlign: "center" })) return "center";
    if (editor.isActive({ textAlign: "right" })) return "right";
    return "left";
  };

  // eslint-disable-next-line
  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !file.type.startsWith("image/")) {
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        if (base64) {
          editor.chain().focus().setImage({ src: base64 }).run();
        }
      };
      reader.readAsDataURL(file);

      event.target.value = "";
    },
    [editor],
  );

  return (
    <Grid container alignItems={"center"} gap={2}>
      {menuBarProps.textFormatting ||
        (menuBarProps.linkFormatting && (
          <Grid item>
            <ToggleButtonGroup aria-label="text formatting" size="small">
              {menuBarProps.textFormatting && (
                <>
                  <ToggleButton
                    value={"bold"}
                    selected={editor.isActive("bold")}
                    aria-label="bold"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    disabled={!editor.can().chain().focus().toggleBold().run()}
                  >
                    <FormatBoldIcon />
                  </ToggleButton>

                  <ToggleButton
                    value={"italic"}
                    selected={editor.isActive("italic")}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    disabled={
                      !editor.can().chain().focus().toggleItalic().run()
                    }
                  >
                    <FormatItalicIcon />
                  </ToggleButton>

                  <ToggleButton
                    value={"strike"}
                    selected={editor.isActive("strike")}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    disabled={
                      !editor.can().chain().focus().toggleStrike().run()
                    }
                  >
                    <StrikethroughSIcon />
                  </ToggleButton>

                  <ToggleButton
                    value={"code"}
                    selected={editor.isActive("code")}
                    onClick={() => editor.chain().focus().toggleCode().run()}
                    disabled={!editor.can().chain().focus().toggleCode().run()}
                  >
                    <CodeIcon />
                  </ToggleButton>
                </>
              )}

              {menuBarProps.linkFormatting && (
                <InsertLinkButton editor={editor} />
              )}
            </ToggleButtonGroup>
          </Grid>
        ))}
      {menuBarProps.fontSize && (
        <Grid item>
          <TextField
            type="number"
            size="small"
            value={editor.getAttributes("textStyle")?.fontSize || 12}
            onChange={(e) =>
              editor.chain().focus().setFontSize(e.target.value).run()
            }
            InputProps={{
              inputProps: {
                max: 100,
                min: 10,
              },
            }}
          />
        </Grid>
      )}
      {menuBarProps.markFormatting && (
        <Grid item>
          <ToggleButtonGroup aria-label="mark formatting" size="small">
            <ToggleButton
              value={"heading1"}
              selected={editor.isActive("heading", { level: 1 })}
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 1 }).run()
              }
            >
              <Typography fontWeight={"bold"}>H1</Typography>
            </ToggleButton>

            <ToggleButton
              value={"heading2"}
              selected={editor.isActive("heading", { level: 2 })}
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 2 }).run()
              }
            >
              <Typography fontWeight={"bold"}>H2</Typography>
            </ToggleButton>

            <ToggleButton
              value={"paragraph"}
              selected={editor.isActive("paragraph")}
              onClick={() => editor.chain().focus().setParagraph().run()}
            >
              <Typography fontWeight={"bold"}>P</Typography>
            </ToggleButton>

            {menuBarProps?.textAlign && (
              <>
                <ToggleButton
                  selected={currentAlignment() === "left"}
                  onClick={() => handleAlignment("left")}
                  value="left"
                >
                  <FormatAlignLeftIcon />
                </ToggleButton>
                <ToggleButton
                  selected={currentAlignment() === "center"}
                  onClick={() => handleAlignment("center")}
                  value="center"
                >
                  <FormatAlignCenterIcon />
                </ToggleButton>
                <ToggleButton
                  selected={currentAlignment() === "right"}
                  onClick={() => handleAlignment("right")}
                  value="right"
                >
                  <FormatAlignRightIcon />
                </ToggleButton>
              </>
            )}

            <ToggleButton
              value={"bulletList"}
              selected={editor.isActive("bulletList")}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <FormatListBulletedIcon />
            </ToggleButton>

            <ToggleButton
              value={"orderedList"}
              selected={editor.isActive("orderedList")}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <FormatListNumberedIcon />
            </ToggleButton>

            <ToggleButton
              value={"codeBlock"}
              selected={editor.isActive("codeBlock")}
              onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            >
              <TerminalIcon />
            </ToggleButton>

            {menuBarProps?.addImageFromUpload && (
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                size="small"
                title="Upload image from computer"
              >
                <AddPhotoAlternateIcon />
              </IconButton>
            )}
          </ToggleButtonGroup>
        </Grid>
      )}

      {menuBarProps.markFormatting && (
        <Grid item>
          <ToggleButtonGroup aria-label="mark formatting" size="small">
            <ToggleButton
              value={"blockquote"}
              selected={editor.isActive("blockquote")}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
            >
              <FormatQuoteIcon />
            </ToggleButton>
            <ToggleButton
              value={"horizontalRule"}
              onClick={() => editor.chain().focus().setHorizontalRule().run()}
            >
              <HorizontalRuleIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      )}

      {menuBarProps.markFormatting && (
        <Grid item>
          <ToggleButtonGroup aria-label="mark formatting" size="small">
            <ToggleButton
              value={"clearNodes"}
              onClick={() => editor.chain().focus().clearNodes().run()}
            >
              <FormatClearIcon />
            </ToggleButton>
            <ToggleButton
              value={"setHardBreak"}
              onClick={() => editor.chain().focus().setHardBreak().run()}
            >
              <WrapTextIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      )}

      {menuBarProps.markFormatting && (
        <Grid item>
          <ToggleButtonGroup aria-label="mark formatting" size="small">
            <ToggleButton
              value={"undo"}
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().chain().focus().undo().run()}
            >
              <UndoIcon />
            </ToggleButton>
            <ToggleButton
              value={"redo"}
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().chain().focus().redo().run()}
            >
              <RedoIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      )}

      {menuBarProps.tableFormatting && (
        <Grid item>
          <ToggleButtonGroup aria-label="table formatting" size="small">
            <ToggleButton
              value="insertTable"
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .insertTable({ rows: 1, cols: 2, withHeaderRow: false })
                  .run()
              }
              title="Inserir tabela"
            >
              <TableChartIcon />
            </ToggleButton>

            <ToggleButton
              value="addRowAfter"
              onClick={() => editor.chain().focus().addRowAfter().run()}
              disabled={!editor.can().chain().focus().addRowAfter().run()}
              title="Adicionar linha abaixo"
            >
              <ViewStreamIcon />
            </ToggleButton>

            <ToggleButton
              value="addColumnAfter"
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              disabled={!editor.can().chain().focus().addColumnAfter().run()}
              title="Adicionar coluna à direita"
            >
              <ViewWeekIcon />
            </ToggleButton>

            <ToggleButton
              value="toggleHeaderRow"
              onClick={() => editor.chain().focus().toggleHeaderRow().run()}
              disabled={!editor.can().chain().focus().toggleHeaderRow().run()}
              title="Ativar/Desativar cabeçalho"
            >
              <BorderAllIcon />
            </ToggleButton>

            <ToggleButton
              value="deleteRow"
              onClick={() => editor.chain().focus().deleteRow().run()}
              disabled={!editor.can().chain().focus().deleteRow().run()}
              title="Remover linha"
            >
              <DeleteOutlineIcon fontSize="small" />
              <ViewStreamIcon fontSize="small" />
            </ToggleButton>

            <ToggleButton
              value="deleteColumn"
              onClick={() => editor.chain().focus().deleteColumn().run()}
              disabled={!editor.can().chain().focus().deleteColumn().run()}
              title="Remover coluna"
            >
              <DeleteOutlineIcon fontSize="small" />
              <ViewWeekIcon fontSize="small" />
            </ToggleButton>

            <ToggleButton
              value="deleteTable"
              onClick={() => editor.chain().focus().deleteTable().run()}
              disabled={!editor.can().chain().focus().deleteTable().run()}
              title="Remover tabela"
            >
              <DeleteOutlineIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      )}
      {menuBarProps.selectColor && (
        <MuiColorInput
          size="small"
          onChange={(color) => editor.chain().focus().setColor(color).run()}
          value={editor.getAttributes("textStyle")?.color || "rgb(0, 0, 0)"}
        />
      )}
      {variables.length > 0 && (
        <Grid item>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleClickVariable}
            sx={{
              textTransform: "none",
              color: "text.primary",
              borderColor: "rgba(0, 0, 0, 0.23)",
              "&:hover": {
                borderColor: "text.primary",
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            {t("menu_bar.add_variables")}
          </Button>
          <Menu anchorEl={anchorEl} open={open} onClose={handleCloseVariable}>
            {variables.map((v) => (
              <MenuItem key={v.code} onClick={() => insertVariable(v.code)}>
                {`${v.name} (${v.code})`}
              </MenuItem>
            ))}
          </Menu>
        </Grid>
      )}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept="image/*"
      />
    </Grid>
  );
};

export default MenuBar;
